import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type Lesson, type Track } from '@/api';

export function LessonsList() {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [trackId, setTrackId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.tracks.list().then(setTracks).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    adminApi.lessons.list(trackId || undefined)
      .then(setLessons)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [trackId]);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this lesson? All questions in it will be removed.')) return;
    try {
      await adminApi.lessons.delete(id);
      setLessons((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    if (!trackId) return;
    const newItems = [...lessons];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= newItems.length) return;
    [newItems[index], newItems[swap]] = [newItems[swap]!, newItems[index]!];
    const ids = newItems.map((l) => l.id);
    try {
      await adminApi.lessons.reorder(trackId, ids);
      setLessons(newItems);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    }
  };

  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Lessons</h1>
        <Link
          to="/lessons/new"
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Add lesson
        </Link>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">Filter by track</label>
        <select
          value={trackId}
          onChange={(e) => setTrackId(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 max-w-xs"
        >
          <option value="">All tracks</option>
          {tracks.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Track</th>
                {trackId && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase w-24">Reorder</th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {lessons.map((l, idx) => (
                <tr key={l.id}>
                  <td className="px-4 py-3 text-slate-800 font-medium">{l.title}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {tracks.find((t) => t.id === (l as Lesson & { trackId?: string }).trackId)?.name ?? (l as Lesson & { trackId?: string }).trackId}
                  </td>
                  {trackId && (
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleMove(idx, 'up')}
                          disabled={idx === 0}
                          className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(idx, 'down')}
                          disabled={idx === lessons.length - 1}
                          className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/lessons/${l.id}/edit`}
                      className="text-indigo-600 hover:underline text-sm font-medium mr-4"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(l.id)}
                      className="text-red-600 hover:underline text-sm font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
